import { Component, DestroyRef, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { Router } from '@angular/router';
import {
  GridOptions,
  GridApi,
  ColDef,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { PurchaseOptimizerColumnDef } from './columnDefs';
import { PurchaseOptimizerItem } from 'src/app/shared/models/general';
import { SharedService } from 'src/app/shared/services/shared.service';
import { ModalType, Questions, Answers } from 'src/app/shared/models/constants';
import { InventoryRequestService } from '../inventory-request-lists/inventory-request.service';
import { Subscription } from 'rxjs';
import { DispoEditBtnsComponent } from 'src/app/shared/components/ag-grid/dispo-edit-btns/dispo-edit-btns.component';

@Component({
  selector: 'app-purchase-optimizer',
  standalone: true,
  templateUrl: './purchase-optimizer.component.html',
  styleUrls: ['./purchase-optimizer.component.scss'],
  imports: [AgGridAngular, CurrencyPipe],
})
export class PurchaseOptimizerComponent implements OnInit {
  // ag-grid properties
  gridApi!: GridApi;
  theme = agGridDarkTheme;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,};
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };

  columnDefs: ColDef[] = PurchaseOptimizerColumnDef;
  defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    wrapHeaderText: true,
  };
  rowData: any[] = [];
  shippingMap: { [vendor: string]: number } = {};
  vendorTotals: {
    vendor: string;
    itemTotal: number;
    shipping: number;
    orderTotal: number;
  }[] = [];
  private destroyRef = inject(DestroyRef);
  loading: boolean = false;
  loadingMessage: string = '';
  private cdr = inject(ChangeDetectorRef);
  refreshSub: Subscription;
  components: any = {
    editDeleteBtns: DispoEditBtnsComponent,
  };

  constructor(
    private router: Router,
    public sharedService: SharedService,
    private inventoryRequestService: InventoryRequestService,
  ) {
    this.gridOptions.headerHeight = 40;
    this.gridOptions.rowHeight = 25;
    this.gridOptions.enableCellTextSelection = true;
    this.gridOptions.ensureDomOrder = true;
    this.gridOptions.suppressClipboardPaste = false;
    this.refreshSub = this.inventoryRequestService.refreshRequests$.subscribe(
      () => {
        this.refreshData();
      },
    );
    this.sharedService.yes_no_submit$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response.answer === Answers.yes) {
          if (response.question === Questions.reset_optimizer) {
            this.executeResetOptimizer();
          } else if (response.question === Questions.delete_purchase_optimizer_item) {
            this.deleteItem(response.optional_details);
          }
        }
      });
  }

  ngOnInit(): void {
    this.refreshData();
    this.addButtonColumn();
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
  }

  refreshData(): void {
    if (this.gridApi) {
      this.gridApi.showLoadingOverlay();
    }
    const companyId =
      this.sharedService.company_info?.company_id;
    this.inventoryRequestService
      .getPurchaseOptimizerItems(companyId)
      .subscribe({
        next: (res) => {
          this.rowData = res.data || [];
          this.shippingMap = res.shippingMap || {};
          this.calculateVendorTotals();
          if (this.gridApi) {
            this.gridApi.hideOverlay();
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching optimizer items:', err);
          if (this.gridApi) {
            this.gridApi.hideOverlay();
          }
          this.cdr.detectChanges();
        },
      });
  }

  openVendorDashboard(): void {
    this.router.navigate(['vendor-dashboard']);
  }

  calculateVendorTotals() {
    const totalsMap: { [vendor: string]: number } = {};
    this.rowData.forEach((item) => {
      if (item.vendor_name && item.vendor_cost) {
        totalsMap[item.vendor_name] =
          (totalsMap[item.vendor_name] || 0) +
          item.vendor_cost * (item.desired_qty || 1);
      }
    });

    this.vendorTotals = Object.keys(totalsMap).map((vendor) => {
      const itemTotal = totalsMap[vendor];
      const shipping = this.shippingMap[vendor] || 0;
      return {
        vendor,
        itemTotal,
        shipping,
        orderTotal: itemTotal + shipping,
      };
    });
  }

  addButtonColumn() {
    const columnDefs = [this.columnDefs];
    for (const colDef of columnDefs) {
      const hasEditDelete = colDef.some(
        (column) => column.headerName === 'Edit / Delete',
      );

      if (!hasEditDelete) {
        colDef.push({
          // Edit / Delete
          headerName: 'Edit / Delete',
          field: 'edit_delete',
          resizable: true,
          width: 140,
          pinned: 'right',
          cellRenderer: 'editDeleteBtns',
          cellRendererParams: {
            editFirearm: this.editItemClicked.bind(this),
            deleteFirearm: this.deleteOptimizerItemClicked.bind(this),
          },
        });
      }
    }
  }
  // button click functions
  editItemClicked(selected_row: {
    colValues: PurchaseOptimizerItem;
    rowIndex: number;
  }) {
    this.sharedService.openOptimizerItemModal(
      ModalType.edit,
      selected_row['colValues'],
    );
  }

  addItem() {
    this.sharedService.openOptimizerItemModal();
  }

  optimizeOrder() {
    if (this.gridApi) {
      this.gridApi.showLoadingOverlay();
    }
    this.loading = true;
    this.loadingMessage = 'Minimizing order costs...';
    const companyId = this.sharedService.company_info?.company_id;
    this.inventoryRequestService
      .createOptimizedSalesOrder(companyId)
      .subscribe({
        next: (res) => {
          console.warn(res);

          this.refreshData();

          this.sharedService.raiseSuccessToast(
            'Sales order created successfully!',
          );
          if (this.gridApi) {
            this.gridApi.hideOverlay();
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.loading = false;
          console.error(err);
          if (this.gridApi) {
            this.gridApi.hideOverlay();
          }
          this.cdr.detectChanges();
        },
      });
  }

  resetOptimizer() {
    this.sharedService.openYesNoModal(Questions.reset_optimizer);
  }

  executeResetOptimizer() {
    if (this.gridApi) {
      this.gridApi.showLoadingOverlay();
    }
    this.loading = true;
    this.loadingMessage = 'Resetting optimization results...';
    const companyId =
      this.sharedService.company_info?.company_id;
    this.inventoryRequestService
      .resetPurchaseOptimization(companyId)
      .subscribe({
        next: (res) => {
          this.sharedService.raiseSuccessToast(
            'Optimization results reset successfully',
          );
          this.refreshData();
          if (this.gridApi) {
            this.gridApi.hideOverlay();
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.loading = false;
          this.sharedService.raiseErrorToast(
            'Failed to reset optimization results',
          );
          console.error(err);
          if (this.gridApi) {
            this.gridApi.hideOverlay();
          }
          this.cdr.detectChanges();
        },
      });
  }

  deleteOptimizerItemClicked(selected_row: {
    colValues: PurchaseOptimizerItem;
    rowIndex: number;
  }) {
    this.sharedService.openYesNoModal(
      Questions.delete_purchase_optimizer_item,
      selected_row.colValues,
    );
  }

  deleteItem(item: any) {
    if (!item || !item._id) return;

    this.inventoryRequestService
      .deletePurchaseOptimizerItem(item._id)
      .subscribe({
        next: () => {
          this.sharedService.raiseSuccessToast(
            'Purchase Optimizer Item deleted successfully!',
          );
          this.refreshData();
        },
        error: (err) => {
          this.sharedService.raiseErrorToast('Failed to delete item.');
          console.error(err);
        },
      });
  }

  ngOnDestroy(): void {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
    }
  }
}
